import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GST_TREATMENTS,
  GST_TREATMENT_LABELS,
  INDIAN_STATES,
  isValidGstinChecksum,
  stateOfGstin,
  type Address,
} from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useBaseCurrency, useGstEnabled } from '@/hooks/useOrgSettings';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { CurrencySelect } from '@/features/common/CurrencyRate';
import { Button, Input, SelectField, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader, Textarea } from '@/features/common/ui';

/**
 * A customer, created or edited: the one form for both.
 *
 * ── THE SHAPE, AND WHERE IT COMES FROM ──
 * The sections follow the form a person migrating from Zoho already knows -
 * who they are, their tax facts, how they are billed, where they are, and
 * remarks - with the fields Fint actually stores and prints. Nothing here is
 * decoration: the GST treatment decides which GSTR-1 table their invoices
 * land in, the place of supply decides CGST+SGST against IGST, the billing
 * address prints on every invoice, the currency is what their invoices
 * start in.
 *
 * ── EDITING ──
 * Everything is free to change except what a booked figure rests on. The
 * currency is locked once a document or a payment carries it (Zoho's rule,
 * for the same reason): the way to invoice in another currency is the
 * picker on the invoice. Every edit goes on the activity log with what
 * changed - the API records before and after per field.
 */

/** What the API hands back for a customer - enough to pick them, or to edit them. */
export interface CustomerRecord {
  id: string;
  displayName: string;
  companyName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  currency?: string | null;
  paymentTermId?: string | null;
  gstTreatment?: string | null;
  gstin?: string | null;
  pan?: string | null;
  placeOfSupply?: string | null;
  taxRegistrationNumber?: string | null;
  billingAddress?: Partial<Address> | null;
  shippingAddress?: Partial<Address> | null;
  notes?: string | null;
  openingBalance?: string | null;
  hasTransactions?: boolean;
}

const EMPTY_ADDRESS: Required<Pick<Address, 'attention' | 'line1' | 'line2' | 'city' | 'state' | 'postalCode' | 'country'>> = {
  attention: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
};
type AddressForm = typeof EMPTY_ADDRESS;

const addressForm = (a: Partial<Address> | null | undefined): AddressForm => ({
  attention: a?.attention ?? '',
  line1: a?.line1 ?? '',
  line2: a?.line2 ?? '',
  city: a?.city ?? '',
  state: a?.state ?? '',
  postalCode: a?.postalCode ?? '',
  country: a?.country ?? '',
});
const filled = (a: AddressForm) => Object.values(a).some((v) => v.trim() !== '');
/** Only the fields with something in them, or nothing at all - an empty object would print a blank block. */
const addressOut = (a: AddressForm): Partial<Address> | undefined => {
  const out: Partial<Address> = {};
  for (const [key, value] of Object.entries(a)) if (value.trim()) (out as Record<string, string>)[key] = value.trim();
  return Object.keys(out).length ? out : undefined;
};

export function CustomerSheet({
  customerId,
  onClose,
  onSaved,
}: {
  /** Absent: a new customer. Present: that customer, loaded and edited in place. */
  customerId?: string;
  onClose: () => void;
  onSaved: (customer: CustomerRecord) => void;
}) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const gstEnabled = useGstEnabled();
  const baseCurrency = useBaseCurrency();
  const editing = !!customerId;

  const existing = useQuery({
    queryKey: ['customer', org?.id, customerId],
    queryFn: () => api.get<CustomerRecord>(`/customers/${customerId}`),
    enabled: !!org?.id && !!customerId,
  });
  const terms = useQuery({
    queryKey: ['settings', org?.id],
    queryFn: () => api.get<{ paymentTerms?: Array<{ id: string; label: string; netDays: number; isDefault?: boolean }> }>('/settings'),
    enabled: !!org?.id,
  });

  const [form, setForm] = useState({
    displayName: '',
    companyName: '',
    email: '',
    phone: '',
    website: '',
    gstin: '',
    pan: '',
    gstTreatment: 'consumer',
    placeOfSupply: '',
    taxRegistrationNumber: '',
    currency: '',
    paymentTermId: '',
    openingBalance: '',
    notes: '',
  });
  const [billing, setBilling] = useState<AddressForm>(EMPTY_ADDRESS);
  const [shipping, setShipping] = useState<AddressForm>(EMPTY_ADDRESS);
  const [shipsElsewhere, setShipsElsewhere] = useState(false);
  const [filledFrom, setFilledFrom] = useState<string | null>(null);

  // The stored customer, once, into the form.
  useEffect(() => {
    const c = existing.data;
    if (!c || filledFrom === c.id) return;
    setFilledFrom(c.id);
    setForm({
      displayName: c.displayName,
      companyName: c.companyName ?? '',
      email: c.email ?? '',
      phone: c.phone ?? '',
      website: c.website ?? '',
      gstin: c.gstin ?? '',
      pan: c.pan ?? '',
      gstTreatment: c.gstTreatment ?? 'consumer',
      placeOfSupply: c.placeOfSupply ?? '',
      taxRegistrationNumber: c.taxRegistrationNumber ?? '',
      currency: c.currency ?? '',
      paymentTermId: c.paymentTermId ?? '',
      openingBalance: c.openingBalance ?? '',
      notes: c.notes ?? '',
    });
    setBilling(addressForm(c.billingAddress));
    const ship = addressForm(c.shippingAddress);
    setShipping(ship);
    setShipsElsewhere(filled(ship));
  }, [existing.data, filledFrom]);

  const payload = () => ({
    displayName: form.displayName,
    companyName: form.companyName || (editing ? '' : undefined),
    email: form.email || (editing ? '' : undefined),
    phone: form.phone || (editing ? '' : undefined),
    website: form.website || (editing ? '' : undefined),
    // Only sent when GST is on. An org outside India has no treatment to
    // record, and sending one would show a GST field on their customer.
    gstTreatment: gstEnabled ? form.gstTreatment : undefined,
    gstin: form.gstin || (editing ? '' : undefined),
    pan: form.pan || (editing ? '' : undefined),
    placeOfSupply: gstEnabled ? form.placeOfSupply || undefined : undefined,
    taxRegistrationNumber: !gstEnabled ? form.taxRegistrationNumber || undefined : undefined,
    // The currency is sent only when it is a choice: unchanged on an edit, or the book's on a create, is no field at all.
    currency: editing ? (form.currency && form.currency !== (existing.data?.currency ?? '') ? form.currency : undefined) : form.currency && form.currency !== baseCurrency ? form.currency : undefined,
    paymentTermId: form.paymentTermId || (editing ? null : undefined),
    openingBalance: form.openingBalance.trim() || undefined,
    notes: form.notes || (editing ? '' : undefined),
    billingAddress: addressOut(billing) ?? (editing ? {} : undefined),
    shippingAddress: shipsElsewhere ? (addressOut(shipping) ?? (editing ? {} : undefined)) : editing ? {} : undefined,
  });

  const save = useMutation({
    mutationFn: () => (editing ? api.patch<CustomerRecord>(`/customers/${customerId}`, payload()) : api.post<CustomerRecord>('/customers', payload())),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['customers', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['customer', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['ledger', org?.id] });
      onSaved(saved);
    },
  });

  const error = save.error instanceof ApiError ? save.error : undefined;
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const currencyLocked = editing && !!existing.data?.hasTransactions;
  const title = editing ? `Edit ${existing.data?.displayName ?? 'customer'}` : 'New customer';
  const select = 'min-h-11 w-full min-w-0 rounded-md border border-input bg-background px-2 text-base shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-9 sm:text-sm';

  return (
    <Sheet label={title} onClose={onClose} testId="customer-sheet">
      <SheetHeader title={title} onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          {editing && existing.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}

          <Section title="Who">
            <Input label="Display name" required autoFocus={!editing} value={form.displayName} onChange={set('displayName')} error={error?.fieldError('displayName')} hint="What appears on their invoices and in every picker." />
            <Input label="Company name" value={form.companyName} onChange={set('companyName')} error={error?.fieldError('companyName')} hint="The legal name, when it differs." />
            <Input label="Email" type="email" value={form.email} onChange={set('email')} error={error?.fieldError('email')} />
            <Input label="Phone" value={form.phone} onChange={set('phone')} error={error?.fieldError('phone')} hint="With the country code - it is what WhatsApp sharing dials." />
            <Input label="Website" value={form.website} onChange={set('website')} error={error?.fieldError('website')} placeholder="https://" />
          </Section>

          <Section title="Tax">
            {gstEnabled ? (
              <>
                <div className="space-y-1">
                  <label htmlFor="gst-treatment" className="text-sm font-medium">
                    GST treatment
                  </label>
                  <select id="gst-treatment" value={form.gstTreatment} onChange={(e) => setForm((f) => ({ ...f, gstTreatment: e.target.value }))} className={select} aria-describedby="gst-treatment-help">
                    {GST_TREATMENTS.map((treatment) => (
                      <option key={treatment} value={treatment}>
                        {GST_TREATMENT_LABELS[treatment]}
                      </option>
                    ))}
                  </select>
                  <p id="gst-treatment-help" className="text-xs text-muted-foreground">
                    Registered businesses go into GSTR-1 invoice by invoice. Everyone else is summarised. Entering a valid GSTIN sets this for you.
                  </p>
                </div>
                <div className="space-y-1">
                  <label htmlFor="place-of-supply" className="text-sm font-medium">
                    Place of supply
                  </label>
                  <select id="place-of-supply" value={form.placeOfSupply} onChange={(e) => setForm((f) => ({ ...f, placeOfSupply: e.target.value }))} className={select} aria-describedby="place-of-supply-help">
                    <option value="">Same as your own state</option>
                    {INDIAN_STATES.map((state) => (
                      <option key={state.code} value={state.code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                  <p id="place-of-supply-help" className="text-xs text-muted-foreground">
                    Decides CGST + SGST within your state versus IGST outside it. For an unregistered customer, the state of their address.
                  </p>
                </div>
              </>
            ) : (
              <Input label="Tax registration number" value={form.taxRegistrationNumber} onChange={set('taxRegistrationNumber')} hint="Their VAT / TRN / ABN, printed on their invoices." />
            )}
            <Input
              label="GSTIN"
              value={form.gstin}
              onChange={(e) => {
                const gstin = e.target.value.toUpperCase();
                setForm((f) => ({
                  ...f,
                  gstin,
                  // Nobody types a GSTIN for a consumer. Once one is complete
                  // and passes its checksum the treatment follows - but only
                  // while it is still the default, so a deliberate "SEZ" is
                  // never overwritten.
                  gstTreatment: f.gstTreatment === 'consumer' && isValidGstinChecksum(gstin) ? 'business_gst' : f.gstTreatment,
                  // The registration state is the right default for the place
                  // of supply, and the wrong override of one chosen by hand.
                  placeOfSupply: f.placeOfSupply || stateOfGstin(gstin)?.code || '',
                }));
              }}
              error={error?.fieldError('gstin')}
              hint="Checked against its checksum - a typo here reaches their accountant."
              className="font-mono"
              maxLength={15}
            />
            <Input label="PAN" value={form.pan} onChange={(e) => setForm((f) => ({ ...f, pan: e.target.value.toUpperCase() }))} error={error?.fieldError('pan')} className="font-mono" maxLength={10} hint="Printed on TDS documents." />
          </Section>

          <Section title="Billing">
            <CurrencySelect
              id="customer-currency"
              value={form.currency || baseCurrency}
              onChange={(code) => setForm((f) => ({ ...f, currency: code }))}
              base={baseCurrency}
              disabled={currencyLocked}
              hint={
                currencyLocked
                  ? `Locked: ${existing.data?.displayName} already has invoices or payments in ${existing.data?.currency ?? baseCurrency}. To invoice in another currency, pick it on the invoice.`
                  : 'Invoices to this customer start in this currency. Each invoice can still choose.'
              }
            />
            <SelectField id="customer-terms" label="Payment terms" value={form.paymentTermId} onChange={(v) => setForm((f) => ({ ...f, paymentTermId: v }))}>
              <option value="">The book's default</option>
              {(terms.data?.paymentTerms ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                  {t.isDefault ? ' (default)' : ''}
                </option>
              ))}
            </SelectField>
            <Input
              label="Opening balance"
              inputMode="decimal"
              value={form.openingBalance}
              onChange={set('openingBalance')}
              error={error?.fieldError('openingBalance')}
              hint="What they already owed when the books moved into Fint. Positive means they owe you."
            />
          </Section>

          <Section title="Billing address">
            <AddressFields value={billing} onChange={setBilling} prefix="billing" />
            {/* A hint, never a block: the address may genuinely come later. The
                country is the shipping one's when they ship elsewhere, as the
                invoice prints it. */}
            {gstEnabled && form.gstTreatment === 'overseas' && !billing.country.trim() && !(shipsElsewhere && shipping.country.trim()) ? (
              <p className="text-xs text-warning" data-testid="customer-export-country-hint">
                An export invoice must show the customer&rsquo;s address and country of destination (CGST Rule 46). Fill in the country.
              </p>
            ) : null}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="size-4" checked={shipsElsewhere} onChange={(e) => setShipsElsewhere(e.target.checked)} data-testid="customer-ships-elsewhere" />
              Ships to a different address
            </label>
            {shipsElsewhere ? (
              <div className="space-y-3 rounded-md border border-border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Shipping address</p>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShipping({ ...billing })}>
                    Copy billing address
                  </Button>
                </div>
                <AddressFields value={shipping} onChange={setShipping} prefix="shipping" />
              </div>
            ) : null}
          </Section>

          <Section title="Remarks">
            <Textarea label="Notes" rows={3} value={form.notes} onChange={set('notes')} placeholder="Anything worth knowing about them. Never printed." />
          </Section>

          {error && !error.errors?.length ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={save.isPending} disabled={editing && existing.isLoading} data-testid="customer-save">
            {editing ? 'Save changes' : 'Add customer'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-3">
      <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</legend>
      {children}
    </fieldset>
  );
}

function AddressFields({ value, onChange, prefix }: { value: AddressForm; onChange: (next: AddressForm) => void; prefix: string }) {
  const set = (key: keyof AddressForm) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, [key]: e.target.value });
  return (
    <div className="grid gap-3 [&>*]:min-w-0 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Input label="Attention" value={value.attention} onChange={set('attention')} id={`${prefix}-attention`} />
      </div>
      <div className="sm:col-span-2">
        <Input label="Address line 1" value={value.line1} onChange={set('line1')} id={`${prefix}-line1`} />
      </div>
      <div className="sm:col-span-2">
        <Input label="Address line 2" value={value.line2} onChange={set('line2')} id={`${prefix}-line2`} />
      </div>
      <Input label="City" value={value.city} onChange={set('city')} id={`${prefix}-city`} />
      <Input label="State" value={value.state} onChange={set('state')} id={`${prefix}-state`} />
      <Input label="Postal code" value={value.postalCode} onChange={set('postalCode')} id={`${prefix}-postal`} />
      <Input label="Country" value={value.country} onChange={set('country')} id={`${prefix}-country`} placeholder="India" />
    </div>
  );
}
